import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import apiService from '../services/api';

type ReviewRow = {
  id: string;
  first_name: string;
  last_name?: string;
  user_name?: string;
  avatar_url?: string;
  stars: number;
  text: string;
  relative_date: string;
};

const emptyReview = (): ReviewRow => ({
  id: `review-${Date.now()}`,
  first_name: '',
  last_name: '',
  avatar_url: '',
  stars: 5,
  text: '',
  relative_date: 'il y a 1 mois',
});

const HomeGoogleReviewsPage: React.FC = () => {
  const [rating, setRating] = React.useState(4.9);
  const [reviews, setReviews] = React.useState<ReviewRow[]>([]);
  const [saveMessage, setSaveMessage] = React.useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['google-reviews-config'],
    queryFn: () => apiService.getGoogleReviewsConfig(),
  });

  React.useEffect(() => {
    if (!data?.config) return;
    setRating(Number(data.config.rating ?? 4.9));
    setReviews(
      ((data.config.reviews || []) as ReviewRow[]).map((review, idx) => {
        const firstName = String(review.first_name || '').trim();
        if (firstName) {
          return { ...review, last_name: review.last_name || '' };
        }
        const legacy = String(review.user_name || '').trim();
        const parts = legacy ? legacy.split(/\s+/, 2) : [];
        return {
          ...review,
          id: review.id || `review-${Date.now()}-${idx}`,
          first_name: parts[0] || '',
          last_name: parts[1] || '',
        };
      }),
    );
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiService.updateGoogleReviewsConfig({
        rating,
        reviews,
      }),
    onSuccess: (res) => {
      setSaveMessage(res.message || 'Configuration enregistrée.');
      setTimeout(() => setSaveMessage(null), 2200);
      void refetch();
    },
  });

  const updateReview = (idx: number, patch: Partial<ReviewRow>) => {
    setReviews((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Avis Google (page d&apos;accueil)
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Configurez les avis affichés en carrousel sur le site public.
      </Typography>

      {error && <Alert severity="error">Impossible de charger la configuration.</Alert>}
      {saveMessage && <Alert severity="success" sx={{ mb: 2 }}>{saveMessage}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Note Google /5"
                type="number"
                value={rating}
                onChange={(e) => setRating(Number(e.target.value))}
                inputProps={{ min: 0, max: 5, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || isLoading}
              >
                Enregistrer
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Avis</Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setReviews((prev) => [...prev, emptyReview()])}>
          Ajouter un avis
        </Button>
      </Box>

      {reviews.map((review, idx) => (
        <Card key={review.id || idx} sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Avis #{idx + 1}</Typography>
              <IconButton color="error" onClick={() => setReviews((prev) => prev.filter((_, i) => i !== idx))}>
                <DeleteIcon />
              </IconButton>
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Prénom"
                  value={review.first_name}
                  onChange={(e) => updateReview(idx, { first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Nom"
                  value={review.last_name || ''}
                  onChange={(e) => updateReview(idx, { last_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  fullWidth
                  label="Étoiles"
                  value={review.stars}
                  onChange={(e) => updateReview(idx, { stars: Number(e.target.value) })}
                >
                  {[5, 4, 3, 2, 1].map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Date relative" value={review.relative_date} onChange={(e) => updateReview(idx, { relative_date: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Avatar URL (optionnel)" value={review.avatar_url || ''} onChange={(e) => updateReview(idx, { avatar_url: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={2} label="Commentaire" value={review.text} onChange={(e) => updateReview(idx, { text: e.target.value })} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default HomeGoogleReviewsPage;

